/**
 * Chairman HR Performance Report Page
 * Displays HR performance statistics for the Chairman
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import chairmanReportsService from '../services/chairmanReportsService';
import { toast } from 'react-toastify';

const ChairmanHRPerformancePage = () => {
  const { user } = useAuth();
  const [hrPerformanceData, setHrPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    hrId: ''
  });

  useEffect(() => {
    if (user?.role === 'CHAIRMAN') {
      fetchHRPerformanceData();
    }
  }, [user, filters]);

  const fetchHRPerformanceData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await chairmanReportsService.getHRPerformanceReport(filters);
      
      if (response.success) {
        setHrPerformanceData(response.data);
      } else {
        setError('Failed to load HR performance data');
      }
    } catch (err) {
      console.error('Error fetching HR performance data:', err);
      setError(err.response?.data?.error || 'Failed to load HR performance data');
      toast.error('Failed to load HR performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      hrId: ''
    });
  };

  const exportToCSV = () => {
    if (hrPerformanceData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const headers = [
      'HR Name',
      'Total Applications',
      'Open Applications', 
      'Interviews Taken',
      'Selected',
      'Rejected',
      'Conversion Rate (%)'
    ];

    const csvContent = [
      headers.join(','),
      ...hrPerformanceData.map(hr => [
        hr.hrName,
        hr.totalApplications,
        hr.openApplications,
        hr.interviewsTaken,
        hr.selectedCount,
        hr.rejectedCount,
        hr.conversionRate
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hr-performance-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  if (user?.role !== 'CHAIRMAN') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">HR Performance Report</h1>
          <p className="text-gray-600">Monitor HR employee performance and productivity</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HR ID (Optional)</label>
              <input
                type="text"
                name="hrId"
                value={filters.hrId}
                onChange={handleFilterChange}
                placeholder="Enter HR ID"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex items-end gap-2">
              <button
                onClick={clearFilters}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
              <button
                onClick={exportToCSV}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* HR Performance Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">HR Performance Data</h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {hrPerformanceData.length} HR employees
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading HR performance data...</p>
            </div>
          ) : hrPerformanceData.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No HR performance data found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HR Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applications Assigned
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Open Applications
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interviews Taken
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selected
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rejected
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversion Rate
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {hrPerformanceData.map((hr) => (
                    <tr key={hr.hrId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {hr.hrName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {hr.hrId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {hr.totalApplications}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {hr.openApplications}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {hr.interviewsTaken}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {hr.selectedCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {hr.rejectedCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${Math.min(hr.conversionRate, 100)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">
                            {hr.conversionRate}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Statistics */}
        {hrPerformanceData.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-sm font-medium text-gray-500">Total HRs</div>
              <div className="text-2xl font-bold text-gray-900">{hrPerformanceData.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-sm font-medium text-gray-500">Total Applications</div>
              <div className="text-2xl font-bold text-gray-900">
                {hrPerformanceData.reduce((sum, hr) => sum + hr.totalApplications, 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-sm font-medium text-gray-500">Total Interviews</div>
              <div className="text-2xl font-bold text-gray-900">
                {hrPerformanceData.reduce((sum, hr) => sum + hr.interviewsTaken, 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-sm font-medium text-gray-500">Avg Conversion Rate</div>
              <div className="text-2xl font-bold text-gray-900">
                {hrPerformanceData.length > 0 
                  ? Math.round((hrPerformanceData.reduce((sum, hr) => sum + hr.conversionRate, 0) / hrPerformanceData.length) * 100) / 100
                  : 0}%
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ChairmanHRPerformancePage;